// ServiceWorkerMessenger.js
'use strict';

import env from '../resource/env.js';
import WorkerMessenger from '../resource/WorkerMessenger.js';
import unRegisterServiceWorker from '../resource/unRegisterServiceWorker.js';
import Delay from '../resource/Delay.js';
import { workerMessenger as workerMessengerDebug } from '../resource/debug.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';

const isInBrowserMainThread = getIsInBrowserMainThread();
const workerMessengerLog = workerMessengerDebug.extend('log');
const workerMessengerErrorLog = workerMessengerDebug.extend('error');

export const messengerMessageIdKey = 'swMessageId';
let urlTimeout = null;
if (isInBrowserMainThread) {
  urlTimeout = new URL(window.location.href).searchParams.get(
    'serviceWorkerTimeout'
  );
}

export const defaultGetInstanceArgument = {
  messengerArguments: {
    name: 'serviceWorker',
    scope: {},
    messageIdKey: messengerMessageIdKey,
  },
  registerArguments: [
    isInBrowserMainThread ? window.serviceWorkerPath : '',
    { scope: '/' },
  ],
  fallbackTimeoutMsec: urlTimeout || env.SERIVCE_WORKER_FALLBACK_TIMEOUT_MSEC,
};

/**
 * ServiceWorkerMessenger extends WorkerMessenger, adding an event queue for
 * calls made before the service worker takes control.
 */
class ServiceWorkerMessenger extends WorkerMessenger {
  /**
   * Create a serviceWorkerMessenger.
   * @param {string} {messengerArguments.name} - Name for worker messenger, mostly for logging.
   * @param {object} {[messengerArguments.scope={}]} - Scope for peer interfaces to run.
   * @param {string} {messengerArguments.messageIdKey} - Message id property name on message object, to identify message from other purpose events.
   * @param {array} {registerArguments} - arguments for navigator.serviceWorker.register()
   * @param {number} {fallbackTimeoutMsec = 5000} - how long to wait for the service worker to take control before giving up on the initial registration race
   */
  constructor({
    messengerArguments: { name, scope, messageIdKey },
    registerArguments,
    fallbackTimeoutMsec = 5000,
  }) {
    super({
      name,
      scope,
      messageIdKey,
      listenTarget: globalThis.navigator.serviceWorker,
      getMessageTarget: async () => null,
    });

    this.fallbackTimeoutMsec = fallbackTimeoutMsec;

    super.getMessageTarget = this._getMessageTarget;

    this._callingQueue = [];

    this.isWaitingControllerChange = true;

    this._initServiceWorker({ registerArguments, fallbackTimeoutMsec });
  }

  /**
   * Set value/object to the message target scope
   * @param {array} {selectPath} - node path from scope toward the set target, missing nodes will be create
   * @param {any} {value} - value to be set, will be transform by safeStructuredClone()
   * @param {Object} {[options={}]} - options
   */
  async set(input = {}) {
    if (input.options?.isCritical && this.isWaitingControllerChange) {
      return this._queueCriticalCall({ interfaceName: 'set', input });
    }
    return super.set(input);
  }

  /**
   * Call function in the message target scope, with Function.prototype.apply() pattern
   * @param {array} {selectPath} - node path from scope toward the target function
   * @param {array} {thisSelectPath} - node path from scope toward the 'this' target for apply
   * @param {array} {args} - apply(_, args), each value will be transformed by safeStructuredClone()
   * @param {Object} {[options={}]} - options
   */
  async apply(input = {}) {
    if (input.options?.isCritical && this.isWaitingControllerChange) {
      return this._queueCriticalCall({ interfaceName: 'apply', input });
    }
    return super.apply(input);
  }

  /**
   * Queue an isCritical call so the incoming worker gets it once it takes
   * control.
   * When a controller is already in place (an older worker still serving this
   * page while the new one installs) the call is also sent right away, so the
   * current worker isn't left without critical data.
   * Without a controller there is no message target and no main-thread
   * stand-in any more, so calling the parent now would create an RPC that can
   * never settle and would leak its feedback handler; the caller's promise is
   * settled by the queued call instead.
   * @param {string} {interfaceName} - 'set' | 'apply'
   * @param {object} {input} - the original interface input
   * @return {Promise} promise of the call that actually reaches a worker
   */
  _queueCriticalCall({ interfaceName, input }) {
    this.log(`${interfaceName}() push call`, { input });

    if (globalThis.navigator.serviceWorker.controller) {
      this._callingQueue.push({ interface: interfaceName, input });
      return super[interfaceName](input);
    }

    return new Promise((resolve, reject) => {
      this._callingQueue.push({
        interface: interfaceName,
        input,
        resolve,
        reject,
      });
    });
  }

  async _getMessageTarget() {
    return globalThis.navigator.serviceWorker.controller;
  }

  /**
   * Block destroying the service worker messenger
   */
  destroy() {
    throw new Error("can't destroy a singleton");
  }

  static instance = null;

  /**
   * Create a serviceWorkerMessenger.
   * @param {string} {messengerArguments.name} - Name for worker messenger, mostly for logging.
   * @param {object} {[messengerArguments.scope={}]} - Scope for peer interfaces to run.
   * @param {string} {messengerArguments.messageIdKey} - Message id property name on message object, to identify message from other purpose events.
   * @param {array} {registerArguments} - arguments for navigator.serviceWorker.register()
   * @param {number} {fallbackTimeoutMsec = 5000} - how long to wait for the service worker to take control before giving up on the initial registration race
   * @return {ServiceWorkerMessenger} singleton service worker messenger
   */
  static getInstance = ({
    messengerArguments = defaultGetInstanceArgument.messengerArguments,
    registerArguments = defaultGetInstanceArgument.registerArguments,
    fallbackTimeoutMsec = defaultGetInstanceArgument.fallbackTimeoutMsec,
  } = defaultGetInstanceArgument) => {
    const log = workerMessengerLog.extend('getInstance');
    const errorLog = workerMessengerErrorLog.extend('getInstance');
    if (!this.instance) {
      try {
        log('getInstance()', {
          messengerArguments,
          registerArguments,
          fallbackTimeoutMsec,
        });
        this.instance = new ServiceWorkerMessenger({
          messengerArguments,
          registerArguments,
          fallbackTimeoutMsec,
        });
      } catch (error) {
        errorLog('getInstance() error', {
          error,
          messengerArguments,
          registerArguments,
          fallbackTimeoutMsec,
        });
      }
    }
    return this.instance;
  };

  _flushQueuedCalls() {
    const log = this.log.extend('_flushQueuedCalls');
    const errorLog = this.errorLog.extend('_flushQueuedCalls');

    log('start flushing');
    while (this._callingQueue.length) {
      const call = this._callingQueue.shift();
      log('flushing', { call, remain: this._callingQueue.length });
      try {
        // the queued calls will mostly be promises, need to use
        // .catch() to make sure errors can be caught,
        // not using `await` is to prevent blocking promises
        const result = super[call.interface](call.input);
        result.catch(error => {
          errorLog('error', { error, call });
        });
        // a deferred call: its caller is still holding this promise
        call.resolve?.(result);
      } catch (error) {
        errorLog('error', { error, call });
        call.reject?.(error);
      }
    }
    log('finish flushing');
  }

  _getControllerChangePromise({ inputUrl }) {
    const log = this.log.extend('_getControllerChangePromise');
    return new Promise(resolve => {
      log('start');
      const listener = event => {
        const scriptURL = navigator.serviceWorker.controller?.scriptURL;
        log('service worker changed', {
          inputUrl: inputUrl.href,
          scriptURL,
          event,
        });
        if (inputUrl.href === scriptURL) {
          log('service worker updated, resolve', {
            inputUrl: inputUrl.href,
            scriptURL,
            event,
          });
          navigator.serviceWorker.removeEventListener(
            'controllerchange',
            listener
          );
          resolve(event);
        } else {
          log('service worker changed with different url, skip resolve', {
            inputUrl: inputUrl.href,
            scriptURL,
            event,
          });
        }
      };
      navigator.serviceWorker.addEventListener('controllerchange', listener);
    });
  }

  async _registerServiceWorker(registerArguments) {
    const log = this.log.extend('_registerServiceWorker');
    log('init', { registerArguments });
    // use !Array to use old unregister logic first.
    if (!navigator.serviceWorker.controller && !Array) {
      log('missing controller');
      const registration = await navigator.serviceWorker.getRegistration();
      // sync controller again after await
      const controller = navigator.serviceWorker.controller;
      log('getRegistration()', { registration });

      let refreshBy = null;
      if (!controller && !registration?.active) {
        // sometimes we still got 'new' on hard-reload,
        // but this case could be auto recovered anyways
        refreshBy = 'new';
      } else if (!controller && registration?.active) {
        refreshBy = 'hard-reload';
      } else if (controller && !registration?.active) {
        refreshBy = 'unknown';
      } else {
        refreshBy = 'reload';
      }
      log('check refreshBy', { refreshBy });

      if (refreshBy === 'hard-reload') {
        log('unregister start', { registerArguments, refreshBy });
        await unRegisterServiceWorker();
        log('unregister done', { registerArguments, refreshBy });
      }
    }

    log('register start', { registerArguments });
    const registration = await navigator.serviceWorker.register(
      ...registerArguments
    );
    log('register done', { registerArguments, registration });
    return registration;
  }

  /**
   * The registration race timed out or failed. There is no main-thread
   * stand-in for the service worker any more, so keep the queued (isCritical)
   * calls — notably the remote config sync the SW needs for response
   * decryption — and flush them if a controller eventually takes over.
   */
  _deferQueuedCallsUntilControllerChange() {
    const log = this.log.extend('_deferQueuedCallsUntilControllerChange');
    log('registration race is over, keep waiting for a controller', {
      queueLength: this._callingQueue.length,
    });

    const listener = () => {
      navigator.serviceWorker.removeEventListener('controllerchange', listener);
      log('controller arrived, flush queued calls', {
        queueLength: this._callingQueue.length,
      });
      this.isWaitingControllerChange = false;
      this._flushQueuedCalls();
    };
    navigator.serviceWorker.addEventListener('controllerchange', listener);
  }

  async _initServiceWorker({ registerArguments, fallbackTimeoutMsec } = {}) {
    const log = this.log.extend('_initServiceWorker');
    const errorLog = this.errorLog.extend('_initServiceWorker');

    if (!isInBrowserMainThread) {
      throw new Error(
        'ServiceWorkerMessenger not invoked in browser main thread.'
      );
    }
    if (!navigator.serviceWorker) {
      throw new Error('service worker is not supported.');
    }

    const [inputScriptUrl] = registerArguments || [];
    if (!inputScriptUrl) {
      throw new Error('ServiceWorkerMessenger create without script url.');
    }
    const inputUrl = new URL(inputScriptUrl, location.origin);

    try {
      const currentScriptUrl = navigator.serviceWorker.controller?.scriptURL;

      if (inputUrl.href === currentScriptUrl) {
        log('service worker is up to date', {
          currentScriptUrl,
          inputScriptUrl,
        });
      } else {
        log('service worker need to update', {
          currentScriptUrl,
          inputScriptUrl,
        });
        const promiseRaceResult = await Promise.race([
          Promise.all([
            this._getControllerChangePromise({ inputUrl }),
            this._registerServiceWorker(registerArguments),
          ]),
          new Delay().promise(fallbackTimeoutMsec).then(() => null),
        ]);
        log('done service worker update', {
          currentScriptUrl,
          inputScriptUrl,
          registerArguments,
          promiseRaceResult,
        });

        if (null !== promiseRaceResult) {
          const registeration = await navigator.serviceWorker.ready;
          log('done service worker update ready', {
            registeration,
            serviceWorker: globalThis.navigator.serviceWorker,
            controller: globalThis.navigator.serviceWorker.controller,
          });
        } else {
          this._deferQueuedCallsUntilControllerChange();
          return;
        }
        // should clear this._feedbackHandlers?
        this._flushQueuedCalls();
      }

      this.isWaitingControllerChange = false;
    } catch (error) {
      errorLog('error', { error, registerArguments });
      // registration failed, but a controller may still take over later (a
      // previously installed worker, or a retried registration); keep
      // isWaitingControllerChange on so critical calls stay queued instead of
      // being posted nowhere, and flush them when that happens
      this._deferQueuedCallsUntilControllerChange();
    }
  }
}

export default ServiceWorkerMessenger;
